import React, { useState } from "react";
import {
  Box,
  Card,
  Link as MuiLink,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";

import { InvoicePage } from "./InvoicePage";

const formatDate = (d) => {
  if (!d) return "";
  const date = new Date(d);
  if (isNaN(date.getTime())) return "";
  return date.toLocaleDateString("en-GB");
};

const formatMoney = (v) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "EUR" }).format(
    v || 0
  );

export const InvoicesList = (props) => {
  const theme = useTheme();
  const isCompact = useMediaQuery(theme.breakpoints.down("md"));
  const [selectedItem, setSelectedItem] = useState(null);

  const items = props.invoices?.result || [];

  if (props.showItem) {
    return (
      <InvoicePage
        onBack={() => props.setShowItem(false)}
        item={selectedItem}
      />
    );
  }

  const handleOpen = (it) => {
    setSelectedItem(it);
    props.setShowItem(true);
  };

  const columns = isCompact
    ? [
        { key: "number", label: "No" },
        { key: "postingDate", label: "Date" },
        { key: "vehicleRegistrationNumber", label: "Reg" },
        { key: "amountExclVAT", label: "Subtotal", align: "right" },
      ]
    : [
        { key: "number", label: "No" },
        { key: "postingDate", label: "Date" },
        { key: "vehicleRegistrationNumber", label: "Reg" },
        { key: "location", label: "Location" },
        { key: "locationCode", label: "Warehouse" },
        { key: "fitterName", label: "Fitter" },
        { key: "amountExclVAT", label: "Subtotal", align: "right" },
      ];

  return (
    <Card
      sx={{
        overflow: "hidden",
        border: (t) => `1px solid ${t.palette.divider}`,
        boxShadow: "none",
      }}
    >
      <TableContainer>
        <Table size="medium" stickyHeader>
          <TableHead>
            <TableRow sx={{ bgcolor: "background.default" }}>
              {columns.map((c) => (
                <TableCell
                  key={c.key}
                  align={c.align || "left"}
                  sx={{
                    fontWeight: 700,
                    textTransform: "uppercase",
                    fontSize: 12,
                    letterSpacing: 0.5,
                    bgcolor: "background.default",
                  }}
                >
                  {c.label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {items.map((it) => (
              <TableRow
                key={it.id ?? it.number}
                hover
                onClick={() => handleOpen(it)}
                sx={{ cursor: "pointer", "&:last-of-type td": { borderBottom: 0 } }}
              >
                <TableCell>
                  <MuiLink
                    component="button"
                    type="button"
                    underline="hover"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleOpen(it);
                    }}
                    sx={{
                      color: "primary.main",
                      fontWeight: 600,
                      fontSize: isCompact ? 13 : 14,
                    }}
                  >
                    {it.number}
                  </MuiLink>
                </TableCell>
                <TableCell sx={{ fontSize: isCompact ? 13 : 14 }}>
                  {formatDate(it.postingDate)}
                </TableCell>
                <TableCell sx={{ fontSize: isCompact ? 13 : 14 }}>
                  {it.vehicleRegistrationNumber || "—"}
                </TableCell>
                {!isCompact && <TableCell>{it.location || "—"}</TableCell>}
                {!isCompact && (
                  <TableCell>{it.locationCode || "—"}</TableCell>
                )}
                {!isCompact && (
                  <TableCell>{it.fitterName || "—"}</TableCell>
                )}
                <TableCell align="right" sx={{ fontWeight: 600 }}>
                  {formatMoney(it.amountExclVAT)}
                </TableCell>
              </TableRow>
            ))}
            {items.length === 0 && (
              <TableRow>
                <TableCell
                  colSpan={columns.length}
                  align="center"
                  sx={{ py: 6, color: "text.secondary" }}
                >
                  <Typography variant="body2">No invoices to show</Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Card>
  );
};
